// src/pages/Solutions/SolutionDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, Chip, Button, Grid, Divider,
  Avatar, Rating, Tabs, Tab, Card, CardContent, List, ListItem,
  ListItemIcon, ListItemText, TextField, CircularProgress, Alert,
  Breadcrumbs, Link, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Star, People, QuestionAnswer, TrendingUp,
  AutoAwesome, Verified, WorkspacePremium, RateReview,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import api from '../../services/api';

const RENGIGS = {
  green: '#2E7D32',
  lime: '#CDDC39',
  dark: '#1A2A3A',
  light: '#F8FAFC',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
};

const SolutionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [solution, setSolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [inquiryDialog, setInquiryDialog] = useState(false);
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [inquiryData, setInquiryData] = useState({ subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const [userOrganizations, setUserOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  const [pricePrediction, setPricePrediction] = useState(null);
  const [showPricePrediction, setShowPricePrediction] = useState(true);

  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState({
    overall_rating: 5,
    title: '',
    comments: '',
    pros: '',
    cons: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  // ── Fetch user's organizations ─────────────────────────────────────────────
  const fetchUserOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setUserOrganizations([]);
      setLoadingOrgs(false);
      return;
    }
    setLoadingOrgs(true);
    try {
      let data;
      try {
        data = await api.get('/organizations/my-organizations/');
      } catch {
        data = await api.get('/organizations/');
      }
      let orgs = data.results || data || [];
      if (user?.id && orgs.length > 0 && !orgs[0]?.members) {
        orgs = orgs.filter(org => org.created_by === user.id);
      }
      setUserOrganizations(orgs);
      if (orgs.length > 0) {
        setSelectedOrgId(orgs[0].id);
      } else {
        setSelectedOrgId('');
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  }, [isAuthenticated, user]);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchSolution = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/solutions/${id}/`);
      setSolution(data);
    } catch (err) {
      setError(err.data?.message || 'Failed to load solution');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const data = await api.get(`/solutions/${id}/reviews/`);
      setReviews(data.results || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  const fetchPricePrediction = async () => {
    try {
      const data = await api.get(`/ai/predict-price/${id}/`);
      setPricePrediction(data);
    } catch (err) {
      console.error('Error fetching price prediction:', err);
    }
  };

  const checkIfReviewed = useCallback(() => {
    if (!isAuthenticated || !selectedOrgId || loadingOrgs) {
      setHasReviewed(false);
      return;
    }
    const alreadyReviewed = reviews.some(r => r.organization_id === selectedOrgId);
    setHasReviewed(alreadyReviewed);
  }, [isAuthenticated, selectedOrgId, reviews, loadingOrgs]);

  useEffect(() => {
    checkIfReviewed();
  }, [checkIfReviewed]);

  useEffect(() => {
    fetchSolution();
    fetchReviews();
    fetchPricePrediction();
    fetchUserOrganizations();
  }, [id, fetchUserOrganizations]);

  // ── Review submission – always include organization ───────────────────────
  const handleSubmitReview = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Ensure we have an organization ID
    let orgId = selectedOrgId;
    if (!orgId && userOrganizations.length > 0) {
      orgId = userOrganizations[0].id;
      setSelectedOrgId(orgId);
    }

    if (!orgId) {
      alert('You need to be part of an organization to review. Please create or join an organization first.');
      return;
    }

    try {
      setSubmittingReview(true);
      const payload = {
        overall_rating: reviewData.overall_rating,
        title: reviewData.title,
        comments: reviewData.comments,
        pros: reviewData.pros || '',
        cons: reviewData.cons || '',
        organization: orgId,
      };
      console.log('Submitting review payload:', payload);
      await api.post(`/solutions/${id}/add_review/`, payload);
      
      setReviewDialog(false);
      setReviewData({ overall_rating: 5, title: '', comments: '', pros: '', cons: '' });
      await fetchReviews();
      alert('Review submitted successfully!');
    } catch (err) {
      console.error('Review submission error FULL:', err);
      let errorMsg = 'Failed to submit review';
      if (err.response) {
        errorMsg = JSON.stringify(err.response.data, null, 2);
        console.error('Server response data:', err.response.data);
      } else if (err.request) {
        errorMsg = 'No response from server. Check your network.';
      } else {
        errorMsg = err.message;
      }
      alert(`Error: ${errorMsg}`);
    } finally {
      setSubmittingReview(false);
    }
  };

  // ── Other handlers (unchanged) ───────────────────────────────────────────
  const handleInquirySubmit = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!selectedOrgId) {
      alert('Please select an organization to send an inquiry.');
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/solutions/${id}/create_inquiry/`, { ...inquiryData, organization_id: selectedOrgId });
      setInquiryDialog(false);
      alert('Inquiry sent successfully!');
    } catch (err) {
      console.error(err);
      alert('Failed to send inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!selectedOrgId) {
      alert('Please select an organization to subscribe.');
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/subscriptions/', {
        solution_id: id,
        pricing_plan_id: selectedPlan.id,
        organization_id: selectedOrgId,
        auto_renew: true,
      });
      setSubscriptionDialog(false);
      navigate('/subscriptions');
    } catch (err) {
      console.error(err);
      alert('Failed to create subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleInquiryChange = (e) => setInquiryData({ ...inquiryData, [e.target.name]: e.target.value });
  const handleReviewChange = (e) => setReviewData({ ...reviewData, [e.target.name]: e.target.value });
  const handleBack = () => navigate('/solutions');

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: RENGIGS.green }} />
      </Container>
    );
  }

  if (error || !solution) {
    return (
      <Container>
        <Alert severity="error">{error || 'Solution not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2, color: RENGIGS.dark }}>Back to Solutions</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" sx={{ color: '#64748B', textDecoration: 'none', fontWeight: 500 }}>Home</Link>
        <Link component={RouterLink} to="/solutions" sx={{ color: '#64748B', textDecoration: 'none', fontWeight: 500 }}>Solutions</Link>
        <Typography sx={{ color: RENGIGS.dark, fontWeight: 700 }}>{solution.title}</Typography>
      </Breadcrumbs>

      <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2, color: RENGIGS.dark, fontWeight: 800, textTransform: 'none' }}>Back to Solutions</Button>

      {showPricePrediction && pricePrediction && pricePrediction.predicted_price && (
        <Paper sx={{ mb: 3, p: 2, borderRadius: 2, background: RENGIGS.gradient, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AutoAwesome sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, opacity: 0.9 }}>AI PRICE PREDICTION</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>${pricePrediction.predicted_price}/month</Typography>
              </Box>
            </Box>
            <Button variant="outlined" size="small" onClick={() => setShowPricePrediction(false)} sx={{ borderColor: 'white', color: 'white' }}>Dismiss</Button>
          </Box>
        </Paper>
      )}

      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 2, border: `1px solid ${RENGIGS.border}` }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar src={solution.provider_logo} sx={{ width: 70, height: 70, mr: 2, border: `2px solid ${RENGIGS.lime}` }}>
                {solution.provider_name?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: RENGIGS.dark }}>{solution.title}</Typography>
                <Typography variant="subtitle1" sx={{ color: RENGIGS.green, fontWeight: 700 }}>by {solution.provider_name}</Typography>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ color: '#475569', fontWeight: 500, mb: 2 }}>{solution.tagline}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Rating value={solution.rating_avg || 0} precision={0.5} readOnly sx={{ color: RENGIGS.lime }} />
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>({solution.review_count || 0})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748B' }}>
                <People fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">{solution.subscriber_count || 0} users</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {solution.categories?.map((cat) => (
                <Chip key={cat.id} label={cat.name} sx={{ bgcolor: RENGIGS.dark, color: '#FFF', fontWeight: 600 }} />
              ))}
              {solution.is_verified && <Chip icon={<Verified style={{ color: '#FFF' }} />} label="Verified" sx={{ bgcolor: RENGIGS.green, color: '#FFF', fontWeight: 600 }} />}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ border: `1px solid ${RENGIGS.border}`, borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Pricing Plans</Typography>
                {solution.pricing_plans?.map((plan) => (
                  <Box key={plan.id} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: plan.is_popular ? RENGIGS.lime : RENGIGS.border, borderRadius: 2, position: 'relative' }}>
                    {plan.is_popular && <Chip label="Popular" sx={{ bgcolor: RENGIGS.green, color: '#FFF', fontSize: '0.65rem', height: 20, position: 'absolute', top: -10, right: 10 }} />}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{plan.name}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: RENGIGS.green }}>${plan.price}<Typography component="span" variant="caption" sx={{ color: '#64748B', ml: 0.5 }}>/{plan.billing_cycle || 'mo'}</Typography></Typography>
                    <Button fullWidth variant="contained" sx={{ mt: 2, bgcolor: RENGIGS.green, fontWeight: 800 }} onClick={() => { setSelectedPlan(plan); setSubscriptionDialog(true); }}>Subscribe</Button>
                  </Box>
                ))}
                <Button fullWidth variant="outlined" startIcon={<QuestionAnswer />} sx={{ color: RENGIGS.green, borderColor: RENGIGS.green, fontWeight: 800, mt: 1, textTransform: 'none' }} onClick={() => setInquiryDialog(true)}>Contact Provider</Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: `1px solid ${RENGIGS.border}`, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ bgcolor: RENGIGS.light, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' }, '& .Mui-selected': { color: `${RENGIGS.green} !important` }, '& .MuiTabs-indicator': { bgcolor: RENGIGS.green } }}>
          <Tab label="Overview" />
          <Tab label="Features" />
          <Tab label={`Reviews (${reviews.length})`} />
          <Tab label="Support" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Description</Typography>
              <Typography sx={{ lineHeight: 1.8, color: '#334155' }}>{solution.description}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 4, mb: 1 }}>Key Benefits</Typography>
              <List>
                {solution.benefits?.map((benefit, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle sx={{ color: RENGIGS.green }} /></ListItemIcon>
                    <ListItemText primary={benefit} primaryTypographyProps={{ fontWeight: 500 }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Core Capabilities</Typography>
              <List>
                {solution.key_features?.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><Star sx={{ color: RENGIGS.lime }} /></ListItemIcon>
                    <ListItemText primary={feature} primaryTypographyProps={{ fontWeight: 500 }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ textAlign: 'center', mr: 5 }}>
                    <Typography variant="h2" sx={{ fontWeight: 900, color: RENGIGS.green }}>{solution.rating_avg?.toFixed(1) || '0.0'}</Typography>
                    <Rating value={solution.rating_avg || 0} precision={0.5} readOnly sx={{ color: RENGIGS.lime }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B' }}>{solution.review_count || 0} reviews</Typography>
                  </Box>
                </Box>
                {isAuthenticated && !hasReviewed && (
                  <Button variant="contained" startIcon={<RateReview />} onClick={() => setReviewDialog(true)} sx={{ bgcolor: RENGIGS.green }}>
                    Write a Review
                  </Button>
                )}
              </Box>
              {reviews.length === 0 ? (
                <Alert severity="info">Be the first to review this solution!</Alert>
              ) : (
                reviews.map((review) => (
                  <Card key={review.id} elevation={0} sx={{ mb: 2, border: `1px solid ${RENGIGS.border}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{review.title}</Typography>
                        <Rating value={review.overall_rating} readOnly size="small" sx={{ color: RENGIGS.lime }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>{review.reviewer_name} • {review.organization_name}</Typography>
                      <Typography variant="body1">{review.comments}</Typography>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}

          {tabValue === 3 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Implementation Timeline</Typography>
              <Typography paragraph>{solution.implementation_time || 'Standard 2-4 week deployment.'}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Dialogs */}
      <Dialog open={inquiryDialog} onClose={() => setInquiryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, bgcolor: RENGIGS.gradient, color: 'white' }}>Contact Provider</DialogTitle>
        <DialogContent>
          {userOrganizations.length > 1 && (
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Organization</InputLabel>
              <Select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)} label="Organization">
                {userOrganizations.map(org => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TextField fullWidth margin="normal" name="subject" label="Subject" value={inquiryData.subject} onChange={handleInquiryChange} />
          <TextField fullWidth margin="normal" name="message" label="Message" multiline rows={4} value={inquiryData.message} onChange={handleInquiryChange} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setInquiryDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInquirySubmit} disabled={submitting || !inquiryData.subject || !inquiryData.message} sx={{ bgcolor: RENGIGS.green }}>{submitting ? <CircularProgress size={24} /> : 'Send Inquiry'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, bgcolor: RENGIGS.gradient, color: 'white' }}>Write a Review</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {userOrganizations.length > 1 && (
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Organization</InputLabel>
              <Select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)} label="Organization">
                {userOrganizations.map(org => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Overall Rating *</Typography>
          <Rating value={reviewData.overall_rating} onChange={(e, v) => setReviewData({ ...reviewData, overall_rating: v || 5 })} sx={{ mb: 3, color: RENGIGS.lime }} />
          <TextField fullWidth name="title" label="Review Title *" value={reviewData.title} onChange={handleReviewChange} margin="normal" required />
          <TextField fullWidth multiline rows={3} name="comments" label="Your Review *" value={reviewData.comments} onChange={handleReviewChange} margin="normal" required />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth multiline rows={2} name="pros" label="What worked well (Pros)" value={reviewData.pros} onChange={handleReviewChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth multiline rows={2} name="cons" label="What could be improved (Cons)" value={reviewData.cons} onChange={handleReviewChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitReview} disabled={submittingReview || !reviewData.title || !reviewData.comments} sx={{ bgcolor: RENGIGS.green }}>{submittingReview ? <CircularProgress size={24} /> : 'Submit Review'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={subscriptionDialog} onClose={() => setSubscriptionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, bgcolor: RENGIGS.gradient, color: 'white' }}>Confirm Subscription</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedPlan && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>{selectedPlan.name} Plan</Typography>
              <Paper variant="outlined" sx={{ p: 3, bgcolor: RENGIGS.light, borderRadius: 2, mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: RENGIGS.green }}>${selectedPlan.price}</Typography>
                <Typography sx={{ color: '#64748B', fontWeight: 600 }}>Billing Cycle: {selectedPlan.billing_cycle || 'Monthly'}</Typography>
              </Paper>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Included in this plan:</Typography>
              <List dense>
                {selectedPlan.features?.map((f, i) => (
                  <ListItem key={i}><ListItemIcon sx={{ minWidth: 30 }}><CheckCircle sx={{ color: RENGIGS.green, fontSize: 18 }} /></ListItemIcon><ListItemText primary={f} /></ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSubscriptionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubscribe} disabled={submitting} sx={{ bgcolor: RENGIGS.green }}>{submitting ? <CircularProgress size={24} /> : 'Confirm Subscription'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SolutionDetailPage;